import type { FC } from 'react';
import { useContext } from 'react';
import { Url } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import UploadIcon from '@wix/da-ds/pkg/Icons/24x24/Upload';
import LargeUploadIcon from '@wix/da-ds/pkg/Icons/32x32/Upload';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { useUnlockVideoUploadsCheck } from '@wix/da-submit-shared/pkg/modals/UnlockWithCoreUpsellModal/useUnlockVideoUploadsCheck';
import { BiData, OpenUploadBiEvent } from '@wix/da-bi/pkg/events';
import { useStudioUploadOptions } from '../../hooks/useStudioUploadOptions';

export interface Props {
  label: string;
  linkToSubmit?: boolean;
  className?: string;
  buttonClassName?: string;
}

const SimpleUploadButton: FC<Props> = ({
  label,
  linkToSubmit,
  className,
  buttonClassName,
}) => {
  const isMobile = useContext(MobileContext);

  const { modal: lockedVideoUpsellDialog, ...lockedVideoUploadOptions } =
    useUnlockVideoUploadsCheck();
  const { input, getDropzoneRootProps } = useFileUpload(
    useStudioUploadOptions({ ...lockedVideoUploadOptions })
  );

  const button = isMobile ? (
    <IconButton
      className={buttonClassName}
      size="large"
      icon={LargeUploadIcon}
      iconSizeOverride={IconSize.LARGE}
      biData={BiData<OpenUploadBiEvent>({
        evid: 16,
        sectionname: 'table_header',
      })}
      aria-label={label}
      href={linkToSubmit ? Url.studio({ openSubmitForm: true }) : undefined}
      noPaddings
    />
  ) : (
    <TextButton
      className={buttonClassName}
      size="medium"
      variant="main"
      priority="primary"
      icon={UploadIcon}
      iconProps={{ disableDefaultColors: true }}
      biData={BiData<OpenUploadBiEvent>({
        evid: 16,
        sectionname: 'table_header',
      })}
      href={linkToSubmit ? Url.studio({ openSubmitForm: true }) : undefined}
    >
      {label}
    </TextButton>
  );

  return (
    <>
      <div
        className={className}
        style={{ display: 'flex', alignItems: 'center' }}
        {...(linkToSubmit ? undefined : getDropzoneRootProps())}
      >
        {button}
        {!linkToSubmit && input}
      </div>
      {/* needs to be outside to prevent re-focusing dropzone */}
      {lockedVideoUpsellDialog}
    </>
  );
};
SimpleUploadButton.displayName = 'SimpleUploadButton';

export default SimpleUploadButton;
